import React, { Fragment, useEffect } from 'react'
import styled from '@emotion/styled'

import { grayLight30 } from '../../constants/principles'
import { useToggler, useScroller } from '../../hooks'
import { QuestionUtils, BrowserUtils } from '../../utils/siteUtils'
import { BodyDivFlex } from '../generic/Basics'
import ICOHeader from '../generic/ICOHeader'
import NavFooter from '../generic/navFooter'
import PageHeader from '../generic/PageHeader'
import BoolEnum from '../generic/questions/BoolEnum'
import Currency from '../generic/questions/Currency'
import { historyQuestionnaire } from '../../constants/historyQuestionnaire'
import { AftermarketPageNumber, HistoryPageNumber, VehiclePageNumber, ExteriorPageNumber, ImageCapturePageNumber } from '../../constants'

export default ({
  experienceState,
  offer,
  prospectDocuments,
  getQuestionResponse,
  getQuestionResponseInteger,
  saveQuestionResponse,
  saveQuestionResponseInteger,
  urlState,
  year,
  make,
  model,
  trim,
  images,
  nextClick,
  backClick,
  addImpressions,
  updateGlobalState,
  variation,
  getHasVehicleModifications,
  vinResults,
  qxData,
  historyVerificationData,
  autoCheckData,
  globalState,
  mileage,
  ...otherProps
}) => {
  useEffect(() => {
    import('../compiledSVG/condition/Convertible').then(x => x)
    import('../compiledSVG/condition/Coupe').then(x => x)
    import('../compiledSVG/condition/Generic').then(x => x)
    import('../compiledSVG/condition/Hatchback').then(x => x)
    import('../compiledSVG/condition/Sedan').then(x => x)
    import('../compiledSVG/condition/Suv').then(x => x)
    import('../compiledSVG/condition/Truck').then(x => x)
    import('../compiledSVG/condition/Van').then(x => x)
    import('../compiledSVG/condition/Wagon').then(x => x)
  }, [])
  const didAuctionAnsweredByVehicleActivitiesApi = vinResults ? !vinResults.vehicleActivityApplicationDown : false
  const analytics = []
  const { displayImages, offerCode } = experienceState
  const [setHasScrolled, scroll] = useScroller()
  const [isError, toggleIsError] = useToggler()
  const isDesktop = BrowserUtils.isDesktop()
  const splitItem = item => item.questionId.split('/')
  const getQid = item => splitItem(item)[1]
  const verifiedHistoryQuestionSource = qxData.find(data => data.questionId === 'qx/AreQuestionsVerified')
  const historySubheader = offerCode.toUpperCase() === 'TC' ? 'Answer each question the best you can.' : 'Save time and effort at the dealer. Answer each question the best you can.'
  //Filtered are the questions which shouldn't be shown in funnel and set values to default answers
  //Odors : qp/65
  //BothKeys : qp/79
  //Still Making Payments: qp / 82
  //Insurance Paid for repair : qp / 76
  //Complete Maintenance records: qp / 17
  let filtered =
    prospectDocuments?.documents &&
    prospectDocuments?.documents[0]?.items.filter(
      item =>
        splitItem(item).length <= 2 &&
        item.questionId !== historyQuestionnaire.odors &&
        item.questionId !== historyQuestionnaire.hasBothKeys &&
        item.questionId !== historyQuestionnaire.titleFreeAndClear &&
        item.questionId !== historyQuestionnaire.insuranceClaim &&
        item.questionId !== historyQuestionnaire.serviceRecords
    )
  let answerSourcesQuestion = prospectDocuments && prospectDocuments.wipReadback && prospectDocuments.wipReadback.answers && prospectDocuments.wipReadback.answers.filter(item => item.qRef === 'qx/AnswerSources')
  let answerSourcesArray = null
  if (answerSourcesQuestion && answerSourcesQuestion.length > 0 && answerSourcesQuestion[0].value) {
    answerSourcesArray = JSON.parse(answerSourcesQuestion[0].value)
  }
  const codesOfQuestionsAnswredByAutoCheck = [historyQuestionnaire.originalOwner, historyQuestionnaire.cleanHistoryReport, historyQuestionnaire.rentalCar]
  if (answerSourcesArray && answerSourcesArray.some(item => codesOfQuestionsAnswredByAutoCheck.includes(item.QuestionCode.trim()))) {
    verifiedHistoryQuestionSource.value = 1
  }

  if (filtered && urlState) {
    urlState[historyQuestionnaire.titleFreeAndClear] = { v: 1 } //Still Making Payments
    urlState[historyQuestionnaire.insuranceClaim] = { v: 1 } //Insurance Paid for repair
    urlState[historyQuestionnaire.serviceRecords] = { v: 0 } //Complete Maintenance records
  }

  //Lock question if answered by autocheck
  const lockQuestion = questionId => {
    if (questionId.toLowerCase() === historyQuestionnaire.cleanHistoryReport) {
      if (offer && offer.prospect && offer.prospect.history) {
        const accidentQuestion = offer.prospect.history.find(e => e.questionId === questionId)
        return accidentQuestion?.value?.toLowerCase() === 'yes'
      } else {
        if (autoCheckData?.hasAccidents) {
          return true
        } else {
          return false
        }
      }
    } else if (verifiedHistoryQuestionSource?.value > 0 && (questionId.toLowerCase() === historyQuestionnaire.originalOwner || questionId.toLowerCase() === historyQuestionnaire.rentalCar)) {
      return true
    }
    //Don't lock the auction question if vehicle activities api is down
    if (questionId.toLowerCase() === historyQuestionnaire.auctionedRecently && !didAuctionAnsweredByVehicleActivitiesApi) {
      return false
    }

    return answerSourcesArray ? answerSourcesArray.some(item => item.QuestionCode.toLowerCase() === questionId.toLowerCase()) : false
  }

  const auctionQuestionAnsweredByUser = qxData.find(data => data.questionId === 'qx/AuctionQuestionAnsweredByUser')

  if (filtered && urlState && didAuctionAnsweredByVehicleActivitiesApi) {
    filtered = filtered.filter(item => item.questionId !== historyQuestionnaire.auctionedRecently)
    auctionQuestionAnsweredByUser.value = 0
    urlState[historyQuestionnaire.auctionedRecently] = { v: 1 }
    analytics.push({ key: historyQuestionnaire.auctionedRecently, value: 1 })
  } else {
    auctionQuestionAnsweredByUser.value = 1
  }

  //hide if questions were answered by verified source
  if (urlState && filtered && experienceState && historyVerificationData && verifiedHistoryQuestionSource.value) {
    if (!urlState['vehicleHistoryInfoPopulated']) {
      const { hasAccidents = null, isOriginalOwner = null, usedAsRentalCar = null } = historyVerificationData
      urlState[historyQuestionnaire.cleanHistoryReport] = { v: hasAccidents ? 0 : 1 }
      urlState[historyQuestionnaire.originalOwner] = { v: isOriginalOwner ? 0 : 1 }
      urlState[historyQuestionnaire.rentalCar] = { v: usedAsRentalCar ? 0 : 1 }
      urlState['vehicleHistoryInfoPopulated'] = true

      if (offer && offer.isExpired && offer.canBeResubmitted) {
        const accidentQuestion = offer.prospect.history.find(e => e.questionId === historyQuestionnaire.cleanHistoryReport)
        const answerSourceInputArray = qxData.find(data => data.questionId === 'qx/AnswerSourcesInput')
        const dealerModifiedQuestion = answerSourceInputArray.value.find(item => item.questionCode === 'DealerModifiedAccidentQuestion')

        if (dealerModifiedQuestion.value && accidentQuestion?.value?.toLowerCase() === 'yes' && hasAccidents === false) {
          urlState[historyQuestionnaire.cleanHistoryReport] = { v: 0 }
        }
      }
    }
    if (!experienceState.isAdmin) {
      filtered = filtered.filter(item => item.questionId !== historyQuestionnaire.cleanHistoryReport)
      analytics.push({ key: historyQuestionnaire.cleanHistoryReport, value: urlState[historyQuestionnaire.cleanHistoryReport].v ? 0 : 1 })

      filtered = filtered.filter(item => item.questionId !== historyQuestionnaire.originalOwner)
      analytics.push({ key: historyQuestionnaire.originalOwner, value: urlState[historyQuestionnaire.originalOwner].v ? 0 : 1 })

      filtered = filtered.filter(item => item.questionId !== historyQuestionnaire.rentalCar)
      analytics.push({ key: historyQuestionnaire.rentalCar, value: urlState[historyQuestionnaire.rentalCar].v ? 0 : 1 })
    }
  }

  const childQuestions = prospectDocuments && prospectDocuments.documents && prospectDocuments.documents[0].items.filter(item => !(splitItem(item).length <= 2))
  const parent = filtered && filtered.map(item => ({ parent: item, child: childQuestions.filter(item2 => getQid(item2) === getQid(item)) }))
  const errors = []
  const backStep = getHasVehicleModifications() ? AftermarketPageNumber : VehiclePageNumber

  let nextDisabled = false

  parent &&
    parent.forEach(element => {
      const { questionId } = QuestionUtils.getQuestionLabelAndChoices(element.parent)
      const answer = getQuestionResponse(questionId).v

      !QuestionUtils.isQuestionAnswered(answer) && errors.push(questionId)
      analytics.push({ key: questionId, value: answer })
      nextDisabled = nextDisabled || !QuestionUtils.isQuestionAnswered(answer)

      if (element.child.length && QuestionUtils.isQuestionAnswered(answer) && answer === 0) {
        const { questionId } = QuestionUtils.getQuestionLabelAndChoices(element.child[0])
        !(getQuestionResponseInteger(questionId) > 0) && errors.push(questionId)
        nextDisabled = nextDisabled || !(getQuestionResponseInteger(questionId) > 0)
      }
    })

  addImpressions(analytics)

  const scrollTarget = isError && errors.length && `#${errors[0].replace(/\//gi, '-')}-error`

  useEffect(() => {
    if (isError && scrollTarget) {
      scroll(scrollTarget)
      setHasScrolled(true)
    } else {
      setHasScrolled(true)
      scroll('body', 'top')
    }
  })

  const backDisabled = nextDisabled && urlState.p !== urlState.lp
  const error = () => {
    !isError && toggleIsError()
    setHasScrolled(false)
    updateGlobalState({ fireAnalytics: 'continue_error' })
  }
  const nextStep = urlState && !!urlState.edit ? ExteriorPageNumber : ImageCapturePageNumber

  return (
    <>
      <BodyDivFlex isDesktop={isDesktop}>
        <ICOHeader {...{ displayImages, urlState, year, make, model, images, nextDisabled, errorFunc: error, updateGlobalState, variation, trim, experienceState, globalState, mileage }} />
        <PageHeader text="Vehicle History" subheader={historySubheader} />
        <ContentDiv isDesktop={isDesktop}>
          {parent &&
            parent.map((question, index) => {
              const { questionId } = QuestionUtils.getQuestionLabelAndChoices(question.parent)
              question.parent = isError && errors.find(error => error === questionId) ? { ...question.parent, errorMessage: 'Answer Required' } : question.parent
              return (
                <Fragment key={index}>
                  <InnerContentDiv>
                    <BoolEnum {...{ saveQuestionResponse, question: question.parent, getQuestionResponse, locked: lockQuestion(question.parent.questionId), shouldShowCheck: lockQuestion(question.parent.questionId) }} />
                    {question.child &&
                      getQuestionResponse(questionId).v === 0 &&
                      question.child.map(child => {
                        child = isError && errors.find(error => error === child.questionId) ? { ...child, errorMessage: 'Answer Required' } : child
                        return (
                          <InsuranceDiv key={child.questionId} isDesktop={isDesktop}>
                            <Currency {...{ saveQuestionResponseInteger, getQuestionResponse, getQuestionResponseInteger, question: { ...child, maximumValue: 1000000 } }} />
                          </InsuranceDiv>
                        )
                      })}
                  </InnerContentDiv>
                  <Line isLast={index + 1 !== filtered.length} />
                </Fragment>
              )
            })}
        </ContentDiv>
      </BodyDivFlex>
      <NavFooter
        {...{ experienceState, offer, step: HistoryPageNumber, getQuestionResponse, backStep, globalState, ...otherProps }} //
        backClick={(...e) => (backDisabled ? error() : backClick(...e))}
        nextClick={(...e) => (nextDisabled ? error() : nextClick(nextStep))}
      />
    </>
  )
}

const ContentDiv = styled.div`
  width: ${p => (p.isDesktop ? 80 : 100)}%;
  text-align: center;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding-top: 32px;
`
const InnerContentDiv = styled.div`
  width: 80%;
`
const InsuranceDiv = styled.div`
  width: ${p => (p.isDesktop ? 50 : 100)}%;
  margin: auto;
`
const Line = styled.div`
  border-bottom: ${p => p.isLast && `1px solid ${grayLight30}`};
  margin: 40px 0px;
  width: 100%;
`
